import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { AccountDeletionService } from 'src/app/service/account-deletion.service';
import { AccountService } from 'src/app/service/account.service';
import { AuthService } from 'src/app/service/auth.service';
import { TunerService } from 'src/app/service/tuner.service';
import { formatDeletionDate } from './deletion-date.util';

/**
 * Página de exclusão de conta (/account/delete). URL estável e fora do shell: é a página web
 * dedicada que o Google Play exige no Data safety. O AuthGuard guarda o postLoginRedirect,
 * então quem chega deslogado volta para cá depois de autenticar.
 *
 * Contrato: ftcloud-api/docs/integracoes/frontend-angular/ANGULAR_EXCLUSAO_DE_CONTA.md.
 */
@Component({
  selector: 'app-account-deletion',
  templateUrl: './account-deletion.component.html',
  styleUrl: './account-deletion.component.css',
})
export class AccountDeletionComponent implements OnInit {
  confirmationEmail = '';
  submitting = false;
  emailMismatch = false;
  /** Saldo de cashback já formatado; vazio = a linha do aviso não aparece. */
  cashbackAmount = '';

  done = false;
  scheduledDate = '';

  private userId = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private accountService: AccountService,
    private tunerService: TunerService,
    private accountDeletion: AccountDeletionService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.userId = this.authService.getUserInformations() ?? '';
    this.loadCashback();
  }

  /** Mesmos PDFs do cadastro: só existem em pt e en, então es cai no en. */
  get privacyPolicyUrl(): string {
    return this.isPortuguese
      ? 'assets/docs/politicas-de-privacidade-do-ftcloud-pt-v01.pdf'
      : 'assets/docs/politicas-de-privacidade-do-ftcloud-en-v01.pdf';
  }

  get termsOfUseUrl(): string {
    return this.isPortuguese
      ? 'assets/docs/termos-de-uso-do-ftcloud-pt-v01.pdf'
      : 'assets/docs/termos-de-uso-do-ftcloud-en-v01.pdf';
  }

  private get isPortuguese(): boolean {
    return this.translocoService.getActiveLang() === 'pt-br';
  }

  get canSubmit(): boolean {
    return !this.submitting && this.confirmationEmail.trim().length > 0;
  }

  /**
   * O único endpoint de saldo é o da carteira do tuner, então a linha só vale para tuner com
   * saldo > 0. Qualquer falha omite a linha em silêncio: o aviso não pode travar a página.
   */
  private loadCashback(): void {
    if (!this.userId) return;
    this.accountService.getAccount(this.userId).subscribe({
      next: (account) => {
        if (account.accountType !== 'tuner' || !account.tunerProfileId) return;
        this.tunerService.getWallet(account.tunerProfileId).subscribe({
          next: (wallet) => {
            if (wallet.balance > 0) this.cashbackAmount = this.formatCurrency(wallet.balance, wallet.currency);
          },
          error: () => {},
        });
      },
      error: () => {},
    });
  }

  private formatCurrency(value: number, currency: string): string {
    try {
      return new Intl.NumberFormat(sessionStorage.getItem('language') || undefined, {
        style: 'currency',
        currency,
      }).format(value);
    } catch {
      // Código de moeda inválido: mostra o número puro em vez de esconder o aviso.
      return `${value} ${currency ?? ''}`.trim();
    }
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.submitting = true;
    this.emailMismatch = false;

    // Envia exatamente o que foi digitado: a API ignora caixa e espaços nas pontas.
    this.accountDeletion.request(this.userId, this.confirmationEmail).subscribe({
      next: async (status) => {
        // A data é formatada antes do fim da sessão: o idioma vem do sessionStorage, que o
        // endLocalSession() limpa.
        this.scheduledDate = formatDeletionDate(status.scheduledAt);
        // As sessões já foram derrubadas no servidor; aqui só limpa o estado local, sem
        // navegar, para a mensagem final continuar na tela.
        await this.authService.endLocalSession();
        this.done = true;
        this.submitting = false;
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        if (err.status === 400 && err.error?.error === 'confirmation_email_mismatch') {
          this.emailMismatch = true;
        } else if (err.status === 403 && err.error?.code === 'self_only') {
          this.showError('user.accountDeletion.selfOnly');
        } else {
          this.showError('user.accountDeletion.genericError');
        }
      },
    });
  }

  goBack(): void {
    if (this.userId) {
      this.router.navigate(['/profile', this.userId]);
    } else {
      history.back();
    }
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  private showError(key: string): void {
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate(key) });
  }
}
